import { TableCellWithBadge } from "../../../../data-model/tableCells";
import {
  PrimaryGray,
  SecondaryYellow,
  TertiaryBlue,
  TertiaryCyan,
  TertiaryLime,
  TertiaryMagenta,
  TertiaryRed,
} from "../../../../enums/colors";
import { KeySource, KeySourceLabels } from "../../enums/keySource";

export interface KeySourceBadgeStyle {
  color: string;
  bgColor: string;
}

const EMPTY_KEY_SOURCE_BADGE: KeySourceBadgeStyle = {
  color: PrimaryGray.GRAY_1,
  bgColor: PrimaryGray.GRAY_16,
};

/** Tag colors per KeySource value (text + background from design system enums). */
const KEY_SOURCE_BADGE_COLORS: Record<KeySource, KeySourceBadgeStyle> = {
  [KeySource.CHAT]: {
    color: SecondaryYellow.YELLOW_1,
    bgColor: SecondaryYellow.YELLOW_11,
  },
  [KeySource.OCR]: {
    color: TertiaryCyan.CYAN_3,
    bgColor: TertiaryCyan.CYAN_11,
  },
  [KeySource.BOTH]: {
    color: TertiaryLime.LIME_1,
    bgColor: TertiaryLime.LIME_11,
  },
  [KeySource.CONFLICT]: {
    color: TertiaryMagenta.MAGENTA_4,
    bgColor: TertiaryMagenta.MAGENTA_11,
  },
  [KeySource.MANUAL]: {
    color: TertiaryBlue.BLUE_4,
    bgColor: TertiaryBlue.BLUE_11,
  },
  [KeySource.MISSING]: {
    color: TertiaryRed.RED_600,
    bgColor: TertiaryRed.RED_11,
  },
};

/**
 * Returns true when key_source is null, undefined, or whitespace-only.
 *
 * @param keySource - Raw key_source from the API
 */
export const isKeySourceEmpty = (
  keySource: string | null | undefined,
): boolean => !(keySource ?? "").trim();

/**
 * Formats a key_source value for display in the table tag column.
 *
 * @param keySource - Raw key_source from the API
 * @returns Placeholder when empty; otherwise the mapped or title-cased label
 */
export const formatKeySourceLabel = (
  keySource: string | null | undefined,
): string => {
  if (isKeySourceEmpty(keySource)) {
    return "--";
  }
  const key = keySource!.toLowerCase().trim() as KeySource;
  return KeySourceLabels[key] ?? key.charAt(0).toUpperCase() + key.slice(1);
};

/**
 * Resolves badge colors for a key_source value.
 *
 * @param keySource - Raw key_source from the API
 * @returns Neutral colors when empty; otherwise colors for the known value
 */
export const getKeySourceBadgeColors = (
  keySource: string | null | undefined,
): KeySourceBadgeStyle => {
  if (isKeySourceEmpty(keySource)) {
    return EMPTY_KEY_SOURCE_BADGE;
  }
  const key = keySource!.toLowerCase().trim() as KeySource;
  return KEY_SOURCE_BADGE_COLORS[key] ?? EMPTY_KEY_SOURCE_BADGE;
};

/**
 * Builds the badge cell payload for the Key Source table column.
 *
 * @param keySource - Raw key_source from the API
 * @returns TableCellWithBadge shape for CustomTable
 */
export const buildKeySourceBadgeCell = (
  keySource: string | null | undefined,
): TableCellWithBadge => ({
  text: formatKeySourceLabel(keySource),
  ...getKeySourceBadgeColors(keySource),
});
